import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Settings, Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import { initializeGA4, disableGA4, enableGA4 } from "@/services/googleAnalyticsService";

// Helper to get current consent state
export function getCookieConsent(): { necessary: boolean; analytics: boolean; marketing: boolean } | null {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      return JSON.parse(consent);
    }
  } catch {
    // Invalid JSON or no consent
  }
  return null;
}

// Helper to check if analytics cookies are allowed
export function isAnalyticsAllowed(): boolean {
  const consent = getCookieConsent();
  return consent?.analytics === true;
}

// Helper to check if marketing cookies are allowed
export function isMarketingAllowed(): boolean {
  const consent = getCookieConsent();
  return consent?.marketing === true;
}

// Event für externe Komponenten zum Öffnen des Cookie-Banners
export const openCookieSettings = (): void => {
  window.dispatchEvent(new CustomEvent('open-cookie-settings'));
};

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Block non-essential scripts until consent is given
  const applyConsentSettings = useCallback(async (consent: { analytics: boolean; marketing: boolean }) => {
    // GA4 basierend auf Consent aktivieren/deaktivieren
    if (consent.analytics) {
      await enableGA4();
    } else {
      disableGA4();
    }

    // Dispatch custom event so other parts of the app can react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consent }));
  }, []);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = getCookieConsent();
    if (!consent) {
      setShowBanner(true);
      // Block all non-essential cookies by default (TTDSG compliance)
      // No scripts should be loaded until consent is given
    } else {
      // Apply existing consent settings - initialize GA4 if analytics allowed
      if (consent.analytics) {
        initializeGA4();
      }
    }

    // Event-Listener für externes Öffnen der Cookie-Einstellungen
    const handleOpenSettings = () => {
      setShowBanner(true);
      setShowSettings(true);
    };
    window.addEventListener('open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('open-cookie-settings', handleOpenSettings);
  }, []);

  const acceptAll = async () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    await applyConsentSettings(consent);
    setShowBanner(false);
  };

  const acceptNecessary = async () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    await applyConsentSettings(consent);
    setShowBanner(false);
  };

  const saveCustomSettings = async (settings: { analytics: boolean; marketing: boolean }) => {
    const consent = {
      necessary: true,
      ...settings,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    await applyConsentSettings(consent);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm">
      <div className="fixed bottom-0 left-0 right-0 p-4 md:bottom-4 md:left-4 md:right-auto md:max-w-md">
        <Card className="border border-primary/30 bg-background/95 backdrop-blur-sm shadow-2xl">
          <div className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-full bg-primary/10">
                <Cookie className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-2">Cookies & Datenschutz</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Wir verwenden Cookies, um Ihnen die beste Erfahrung auf unserer Website zu bieten. 
                  Einige sind notwendig, andere helfen uns die Website zu verbessern.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBanner(false)}
                className="p-1 h-auto"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {!showSettings ? (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="primary" onClick={acceptAll} className="flex-1">
                    Alle akzeptieren
                  </Button>
                  <Button variant="outline" onClick={acceptNecessary} className="flex-1 border border-primary hover:shadow-green transition-all duration-300">
                    Nur notwendige
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowSettings(true)}
                    className="flex-1 border border-primary hover:shadow-green transition-all duration-300"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Einstellungen
                  </Button>
                  <Link to="/cookies" className="flex-1">
                    <Button variant="outline" className="w-full border border-primary hover:shadow-green transition-all duration-300">
                      Mehr erfahren
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <CookieSettings onSave={saveCustomSettings} onBack={() => setShowSettings(false)} />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

interface CookieSettingsProps {
  onSave: (settings: { analytics: boolean; marketing: boolean }) => void;
  onBack: () => void;
}

function CookieSettings({ onSave, onBack }: CookieSettingsProps) {
  const savedConsent = getCookieConsent();
  const [analytics, setAnalytics] = useState(savedConsent?.analytics ?? false);
  const [marketing, setMarketing] = useState(savedConsent?.marketing ?? false);

  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-foreground">Cookie-Einstellungen</h4>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div>
            <p className="font-medium text-sm">Notwendige Cookies</p>
            <p className="text-xs text-muted-foreground">Für die Grundfunktionen der Website</p>
          </div>
          <div className="text-sm text-muted-foreground">Immer aktiv</div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div>
            <p className="font-medium text-sm">Analyse Cookies (Google Analytics)</p>
            <p className="text-xs text-muted-foreground">Helfen uns die Website zu verbessern</p>
          </div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-10 h-6 rounded-full transition-colors ${analytics ? 'bg-primary' : 'bg-muted'}`}>
              <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform translate-y-1 ${analytics ? 'translate-x-5' : 'translate-x-1'}`} />
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div>
            <p className="font-medium text-sm">Marketing Cookies</p>
            <p className="text-xs text-muted-foreground">Für personalisierte Werbung</p>
          </div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-10 h-6 rounded-full transition-colors ${marketing ? 'bg-primary' : 'bg-muted'}`}>
              <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform translate-y-1 ${marketing ? 'translate-x-5' : 'translate-x-1'}`} />
            </div>
          </label>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onBack} className="flex-1 border border-primary hover:shadow-green transition-all duration-300">
          Zurück
        </Button>
        <Button variant="primary" onClick={() => onSave({ analytics, marketing })} className="flex-1">
          Speichern
        </Button>
      </div>
    </div>
  );
}
